import * as React from 'react';
import { FC, useCallback, useEffect, useRef } from 'react';

import { Icon, InlineAlert } from '@rbilabs/universal-components';
import { isUndefined } from 'lodash-es';
import { isNumber } from 'lodash/fp';
import { StyleSheet, useWindowDimensions } from 'react-native';

import { ActionButtonVariants } from 'components/action-button';
import { CardModal } from 'components/card-modal';
import { CustomDisclaimer } from 'components/offer-disclaimer/offer-disclaimer';
import { WalmartBadgeContainer } from 'components/offer-list-item/styled';
import Picture from 'components/picture';
import { Box, Typography } from 'components/ui';
import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { useOfferExpiration } from 'hooks/loyalty/use-offer-expiration';
import { useIsDesktopBreakpoint, useIsDesktopLargeBreakpoint } from 'hooks/use-media-query';
import { LoyaltyIncentiveCooldownContent } from 'pages/loyalty/loyalty-incentives-components/loyalty-incentive-cooldown-message';
import { OffersCooldownContainer } from 'pages/loyalty/loyalty-offers/loyalty-offers.styled';
import { useLoyaltyWalmart } from 'pages/loyalty/loyalty-partners/walmart/use-loyalty-walmart';
import { useAuthContext } from 'state/auth';
import { t, tRich } from 'state/intl/translations';
import { FixableEvaluationErrors, IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import {
  getWalmartSeasonalConfigOfferIds,
  isAnyWalmartOffer,
  isWalmartQuarterlyOffer,
} from 'state/loyalty/hooks/utils/walmart';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { isAndroid } from 'utils/environment';
import { TraceName, stopTrace } from 'utils/performance/trace';

import { DISABLE_IN_RESTAURANT_FOR_INVALID_SERVICE_MODE } from '../constants';
import { PointsNeededText, PointsNeededTextDetail } from '../incentive-card/incentive-card.styled';

import {
  AddToCartButton,
  BackgroundImage,
  ButtonContainer,
  ButtonsWrapper,
  DisclaimerText,
  ImageWrapper,
  RedeemInRestaurantButton,
  RemoveFromCartButton,
  RewardDescription,
  RewardDetailsContainer,
  RewardExpiration,
  RewardName,
  StyledDisclaimerContainer,
  StyledIncentiveEvaluationMessages,
  StyledProgressMeter,
  StyledRewardImage,
} from './incentive-details.styled';
import { RewardPoints } from './reward-points';
import { IIncentiveDetailsViewProps } from './types';

const REWARD_IMAGE_HEIGHT = '200px';

const styles = StyleSheet.create({
  evaluationMessageSpacer: {
    height: 16,
  },
  cooldownContent: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: 8,
  },
});

export const IncentiveDetailsView: FC<React.PropsWithChildren<IIncentiveDetailsViewProps>> = ({
  id,
  name,
  image,
  imageDescription,
  description,
  moreInfo,
  isOffer,
  pointCost,
  locked,
  remainingPointsNeededForRedemption,
  redemptionPercentage,
  handleRedeemRewardPress,
  handleInRestaurantRedemptionPress,
  handleViewInCartPress,
  handleShowCodeToRedeemPress,
  errorMessage,
  checkingAvailability,
  backgroundImage,
  canAddToInRestaurantOrder = false,
  canAddToMobileOrder = false,
  isAlreadyAppliedToMobileOrder = false,
  isAlreadyAppliedToInRestaurantOrder = false,
  showRemoveRewardButton = false,
  handleRemoveRewardsPress,
  incentiveEvaluationResults,
  cooldown,
  onLogIncentiveDetailsEvent,
  offerListPosition,
  endDate,
  expirationDate,
  partnerOfferType,
}) => {
  const { isAuthenticated } = useAuthContext();
  const { data: loyaltyWalmartData } = useLoyaltyWalmart();

  const { height } = useWindowDimensions();
  const isDesktop = useIsDesktopBreakpoint();
  const isDesktopLarge = useIsDesktopLargeBreakpoint();
  const loggedIncentiveKeyRef = useRef<string | null>(null);

  const hasServiceModeError = (
    evaluationResults: IIncentiveDetailsViewProps['incentiveEvaluationResults']
  ) =>
    !!evaluationResults?.some(
      evaluationResult =>
        evaluationResult.code === IncentiveEvaluationErrorCodes.INVALID_SERVICE_MODE
    );

  const hasBlockingErrors =
    !!errorMessage ||
    (cooldown.isActive && isOffer) ||
    // Disable buttons if Feedback is not included in fixable incentive errors
    incentiveEvaluationResults?.some(
      evaluationResult =>
        !FixableEvaluationErrors.has(evaluationResult.code as IncentiveEvaluationErrorCodes)
    );

  const showViewInCartCta = isOffer && isAlreadyAppliedToMobileOrder;
  const showMyCodeCta = isOffer && isAlreadyAppliedToInRestaurantOrder;

  const disableMobileOrderButton =
    (!showViewInCartCta && hasBlockingErrors) ||
    (!showViewInCartCta && hasServiceModeError(incentiveEvaluationResults));

  const disableInRestaurantOrderButton =
    (!showMyCodeCta && hasBlockingErrors) ||
    (!showMyCodeCta &&
      DISABLE_IN_RESTAURANT_FOR_INVALID_SERVICE_MODE &&
      hasServiceModeError(incentiveEvaluationResults));

  useEffect(() => {
    if (!checkingAvailability) {
      const disabledMobile = disableMobileOrderButton || !canAddToMobileOrder;
      const disabledInRestaurant = disableInRestaurantOrderButton || !canAddToInRestaurantOrder;
      const isAvailable = !disabledMobile || !disabledInRestaurant;
      const logKey = `${id ?? ''}:${isOffer ? 'offer' : 'reward'}`;

      if (loggedIncentiveKeyRef.current === logKey) {
        return;
      }

      onLogIncentiveDetailsEvent(isAvailable, offerListPosition);
      loggedIncentiveKeyRef.current = logKey;
    }
  }, [
    canAddToInRestaurantOrder,
    canAddToMobileOrder,
    checkingAvailability,
    disableInRestaurantOrderButton,
    disableMobileOrderButton,
    id,
    isOffer,
    offerListPosition,
    onLogIncentiveDetailsEvent,
  ]);

  const addToMobileOrderText = showViewInCartCta ? t('viewInCart') : t('addToCartCarousel');

  const inRestaurantRedemptionText = showMyCodeCta
    ? t('showCodeToRedeem')
    : t('redeemInRestaurant');

  const showWalmartBadge =
    isAnyWalmartOffer({ id }) ||
    isWalmartQuarterlyOffer({ partnerOfferType }) ||
    getWalmartSeasonalConfigOfferIds(loyaltyWalmartData).includes(id);

  const expiresAt = useOfferExpiration(endDate, expirationDate, isAnyWalmartOffer({ id }));

  const headerTextKey = isOffer ? 'offer' : 'reward';
  const headerText = t(headerTextKey);

  const onDisplay = useCallback(() => {
    return stopTrace(isOffer ? TraceName.OFFER_DETAIL_LOAD : TraceName.REWARD_DETAIL_LOAD);
  }, [isOffer]);

  return (
    <CardModal
      key="modal"
      alwaysShowHeader
      webAnimate={!(isOffer && isAndroid)} // the loading semantics of offers makes the animation weird.
      headerText={headerText}
      scrollView
      standardPadding
      isLoading={checkingAvailability}
      footer={
        <>
          {!locked && (
            <ButtonContainer>
              {(canAddToMobileOrder || showViewInCartCta) && (
                <AddToCartButton
                  disabled={disableMobileOrderButton}
                  onPress={showViewInCartCta ? handleViewInCartPress : handleRedeemRewardPress}
                  isLoading={checkingAvailability}
                  isOffer={isOffer}
                  leftIcon={
                    <Icon
                      name="mi:shopping-bag"
                      width="24px"
                      {...hiddenAccessibilityPlatformProps}
                    />
                  }
                >
                  {addToMobileOrderText}
                </AddToCartButton>
              )}

              {(canAddToInRestaurantOrder || showMyCodeCta) && (
                <RedeemInRestaurantButton
                  disabled={disableInRestaurantOrderButton}
                  onPress={
                    showMyCodeCta ? handleShowCodeToRedeemPress : handleInRestaurantRedemptionPress
                  }
                  isLoading={checkingAvailability}
                  isOffer={isOffer}
                  marginTop="$4"
                  leftIcon={
                    <Icon name="custom:BkCode" width="24px" {...hiddenAccessibilityPlatformProps} />
                  }
                  variant={ActionButtonVariants.OUTLINE}
                >
                  {inRestaurantRedemptionText}
                </RedeemInRestaurantButton>
              )}
            </ButtonContainer>
          )}
          {isAuthenticated && showRemoveRewardButton && (
            <ButtonContainer>
              <RemoveFromCartButton onPress={handleRemoveRewardsPress} marginTop="$8">
                {t('removeRewardsFromCart')}
              </RemoveFromCartButton>
            </ButtonContainer>
          )}
        </>
      }
    >
      <ImageWrapper>
        {backgroundImage ? (
          <BackgroundImage alt={imageDescription} image={backgroundImage} />
        ) : null}
        {image ? (
          <StyledRewardImage
            alt={imageDescription}
            image={image}
            width={height / (isDesktop ? 2 : 3)}
            onDisplay={onDisplay}
            {...(!isDesktop && { minHeight: REWARD_IMAGE_HEIGHT })}
          />
        ) : null}
      </ImageWrapper>
      <RewardDetailsContainer>
        {name ? <RewardName>{name}</RewardName> : null}
        {!isUndefined(pointCost) && <RewardPoints pointCost={pointCost} locked={Boolean(locked)} />}
        {description ? <RewardDescription content={description}></RewardDescription> : null}
        {showWalmartBadge && (
          <>
            <Typography variant="copyTwo" mt="$4">
              {t('providedBy')}
            </Typography>
            <WalmartBadgeContainer>
              <Picture
                height="40px"
                image={loyaltyWalmartData?.badge?.locale}
                alt={t('walmartOfferBadgeAlt')}
              />
            </WalmartBadgeContainer>
          </>
        )}
        {locked && (
          <>
            {isNumber(redemptionPercentage) && (
              <StyledProgressMeter percentage={redemptionPercentage} />
            )}
            {isNumber(remainingPointsNeededForRedemption) && (
              <PointsNeededText>
                {tRich('yourXPointsAway', {
                  points: (
                    <PointsNeededTextDetail>
                      {remainingPointsNeededForRedemption}
                    </PointsNeededTextDetail>
                  ),
                })}
              </PointsNeededText>
            )}
          </>
        )}
        {!isOffer && moreInfo && <DisclaimerText content={moreInfo} />}
        {expiresAt && <RewardExpiration>{expiresAt}</RewardExpiration>}
      </RewardDetailsContainer>

      {!!errorMessage && <InlineAlert marginTop="$4" status="info" message={errorMessage} />}

      <ButtonsWrapper
        $errorMessage={!!hasBlockingErrors}
        justifyContent={'center'}
        padding="$0"
        margin="$0"
      ></ButtonsWrapper>

      {incentiveEvaluationResults && !errorMessage && (
        <>
          <Box style={styles.evaluationMessageSpacer} />
          <StyledIncentiveEvaluationMessages
            incentiveEvaluationResults={incentiveEvaluationResults}
            hideIcon
          />
        </>
      )}
      {cooldown.isActive && isOffer && (
        <OffersCooldownContainer>
          <Box style={styles.cooldownContent}>
            <LoyaltyIncentiveCooldownContent
              incentiveType={LoyaltyPromotionType.OFFER}
              availableAfter={cooldown.endsAt}
            />
          </Box>
        </OffersCooldownContainer>
      )}
      {isOffer && moreInfo && (
        <StyledDisclaimerContainer style={{ flex: 0 }} marginTop="$6">
          <Typography
            variant="headerThree"
            fontSize={isDesktopLarge ? 18 : undefined}
            lineHeight={isDesktopLarge ? 22 : undefined}
          >
            {t('offerTerms')}
          </Typography>
          <CustomDisclaimer
            content={moreInfo}
            textProps={{
              textAlign: 'left',
            }}
          />
        </StyledDisclaimerContainer>
      )}
    </CardModal>
  );
};
